import { defineComponent, getCurrentInstance, h } from 'vue';
import set from 'lodash.set';
import merge from 'lodash.merge';

const DOMAIN_SEPARATOR = "__";
const ALL_INDICATOR = "DEEP";
var _sfc_main = defineComponent({
  props: {
    scope: {
      type: String,
      default: null
    },
    slots: {
      type: Boolean,
      default: true
    },
    events: {
      type: Boolean,
      default: false
    },
    properties: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    return () => {
      const { slots } = context;
      const currentInstance = getCurrentInstance();
      const parent = currentInstance == null ? void 0 : currentInstance.proxy.$parent;
      if (!parent) {
        return h("div", "");
      }
      let slotElement = slots.default ? slots.default()[0] : h("div", "");
      const componentName = getComponentName(slotElement);
      if (!componentName) {
        return slotElement;
      }
      if (typeof props.scope === "string") {
        slotElement = applyPrefixScopedSlots(slotElement, props.scope, parent);
      }
      if (props.slots) {
        slotElement = applyComponentScopedSlots(slotElement, componentName, parent);
      }
      if (props.events) {
        slotElement = applyComponentScopedListeners(slotElement, componentName, parent);
      }
      if (props.properties) {
        slotElement = applyComponentScopedProperties(slotElement, componentName, parent);
      }
      return slotElement;
    };
  }
});
const applyPrefixScopedSlots = (slotElement, scope, $parent) => {
  var _a;
  const rootScopedAttributes = getRootScopedAttributes($parent.$scopedSlots, scope);
  if (!shouldUpdateChildSlots(rootScopedAttributes)) {
    return slotElement;
  }
  const childScopedSlots = {};
  rootScopedAttributes.forEach((rootScopedSlot) => {
    const childSlot = $parent.$scopedSlots[rootScopedSlot.attribute];
    childScopedSlots[rootScopedSlot.name] = childSlot;
  });
  const mergeScopedSlots = {};
  merge(mergeScopedSlots, (_a = slotElement.data) == null ? void 0 : _a.scopedSlots, childScopedSlots);
  set(slotElement, "data.scopedSlots", mergeScopedSlots);
  return slotElement;
};
const applyComponentScopedSlots = (slotElement, componentName, $parent) => {
  var _a;
  const componentScopedAttributes = getComponentScopedAttributes($parent.$scopedSlots);
  if (!shouldUpdateChildSlots(componentScopedAttributes)) {
    return slotElement;
  }
  const childScopedSlots = {};
  componentScopedAttributes.forEach((componentScopedSlot) => {
    const childSlot = $parent.$scopedSlots[componentScopedSlot.attribute];
    childScopedSlots[componentScopedSlot.attribute] = childSlot;
    if (componentName === componentScopedSlot.domain) {
      childScopedSlots[componentScopedSlot.name] = childSlot;
    } else if (componentScopedSlot.domain === ALL_INDICATOR) {
      childScopedSlots[componentScopedSlot.name] = childSlot;
      childScopedSlots[ALL_INDICATOR + DOMAIN_SEPARATOR + componentScopedSlot.name] = childSlot;
    }
  });
  const mergeScopedSlots = {};
  merge(mergeScopedSlots, (_a = slotElement.data) == null ? void 0 : _a.scopedSlots, childScopedSlots);
  set(slotElement, "data.scopedSlots", mergeScopedSlots);
  return slotElement;
};
const applyComponentScopedListeners = (slotElement, componentName, $parent) => {
  var _a, _b, _c;
  const mergeListeners = {};
  const childListeners = {};
  const componentListeners = {
    ...(_b = (_a = slotElement.componentOptions) == null ? void 0 : _a.listeners) != null ? _b : {}
  };
  getComponentScopedAttributes($parent.$listeners).forEach((listenerObject) => {
    var _a2, _b2;
    if (componentName === listenerObject.domain) {
      if (((_a2 = slotElement.data) == null ? void 0 : _a2.on) && slotElement.data.on[listenerObject.name]) {
        childListeners[listenerObject.name] = new Array().concat(slotElement.data.on[listenerObject.name], $parent.$listeners[listenerObject.attribute]);
      } else {
        childListeners[listenerObject.name] = $parent.$listeners[listenerObject.attribute];
      }
    } else if (listenerObject.domain === ALL_INDICATOR) {
      if (((_b2 = slotElement.data) == null ? void 0 : _b2.on) && slotElement.data.on[listenerObject.name]) {
        childListeners[ALL_INDICATOR + DOMAIN_SEPARATOR + listenerObject.name] = new Array().concat(
          slotElement.data.on[listenerObject.name],
          $parent.$listeners[listenerObject.attribute]
        );
        childListeners[listenerObject.name] = new Array().concat(slotElement.data.on[listenerObject.name], $parent.$listeners[listenerObject.attribute]);
      } else {
        childListeners[ALL_INDICATOR + DOMAIN_SEPARATOR + listenerObject.name] = $parent.$listeners[listenerObject.attribute];
        childListeners[listenerObject.name] = $parent.$listeners[listenerObject.attribute];
      }
    }
  });
  merge(mergeListeners, (_c = slotElement.data) == null ? void 0 : _c.on, componentListeners, childListeners);
  set(slotElement, "componentOptions.listeners", mergeListeners);
  return slotElement;
};
const applyComponentScopedProperties = (slotElement, componentName, $parent) => {
  var _a, _b;
  const childProperties = {};
  getComponentScopedAttributes($parent.$attrs).forEach((propObject) => {
    if (componentName === propObject.domain) {
      childProperties[propObject.name] = $parent.$attrs[propObject.attribute];
    } else if (propObject.domain === ALL_INDICATOR) {
      childProperties[propObject.name] = $parent.$attrs[propObject.attribute];
      childProperties[ALL_INDICATOR + DOMAIN_SEPARATOR + propObject.name] = $parent.$attrs[propObject.attribute];
    }
  });
  const mergeAttrs = {};
  merge(mergeAttrs, (_a = slotElement.data) == null ? void 0 : _a.attrs, childProperties);
  const mergeProperties = {};
  merge(mergeProperties, (_b = slotElement.componentOptions) == null ? void 0 : _b.propsData, childProperties);
  set(slotElement, "data.attrs", mergeAttrs);
  set(slotElement, "componentOptions.propsData", mergeProperties);
  return slotElement;
};
const getComponentScopedAttributes = (attr) => {
  return Object.keys(attr).filter((slotPath) => {
    const hasDomain = slotPath.split(DOMAIN_SEPARATOR).length > 1;
    return slotPath !== "default" && attr[slotPath] !== void 0 && hasDomain;
  }).map((attribute) => {
    const splitPath = attribute.split(DOMAIN_SEPARATOR);
    const domain = splitPath.shift();
    const name = splitPath.join(DOMAIN_SEPARATOR);
    return { name, domain, attribute };
  });
};
const getRootScopedAttributes = (attr, scope) => {
  return Object.keys(attr).filter((slotPath) => {
    const hasDomain = slotPath.split(DOMAIN_SEPARATOR).length > 1;
    return slotPath !== "default" && attr[slotPath] !== void 0 && !hasDomain && slotPath.startsWith(String(scope));
  }).map((attribute) => {
    const domain = void 0;
    const name = attribute.replace(new RegExp(`^${scope != null ? scope : ""}`), "");
    return { name, domain, attribute };
  });
};
const getComponentName = (vNode) => {
  var _a, _b, _c, _d, _e;
  let name = (_c = (_b = (_a = vNode.componentOptions) == null ? void 0 : _a.Ctor.options) == null ? void 0 : _b.name) != null ? _c : null;
  if (!name && ((_e = (_d = vNode.componentOptions) == null ? void 0 : _d.Ctor.options) == null ? void 0 : _e.__name)) {
    name = vNode.componentOptions.Ctor.options.__name;
  }
  return name ? kebabCase(name) : name;
};
const shouldUpdateChildSlots = (domainScopedAttributes) => {
  return Object.keys(domainScopedAttributes).length > 0;
};
const kebabCase = (string) => string.replace(/([a-z])([A-Z])/g, "$1-$2").replace(/[\s_]+/g, "-").toLowerCase();

export { _sfc_main as default };
